import {
  Asset,
  FeatureName,
  IssueCreationSource,
  IssueType,
  Severity,
  useIsFeatureEnabled,
  useStorageKeys,
} from '@gripp/shared-logic';
import {
  AssetActionButton,
  Colors,
  SeverityIcons,
  TouchableButton,
  colors,
} from '@gripp/shared-ui';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  StyleProp,
  StyleSheet,
  View,
  ViewStyle,
} from 'react-native';
import { NewIssueModal, NewIssueModalProps } from '../issues';
import { localStorageHandler } from '../shared/localStorageHandler';

type ActionButtonsProps = {
  onCompleted: (message: string, messageDownloadApp: string) => void;
  asset: Asset;
  workspaceId: string;
  // Used only by the legacy (non-CC) layout. Kept for callers that still pass
  // them; ignored in the CC branch which manages its own sizing.
  buttonStyle?: StyleProp<ViewStyle>;
  titleFontSize?: number;
};

export const ActionButtons = (props: ActionButtonsProps) => {
  const isCropCircleRedesignEnabled = useIsFeatureEnabled(
    FeatureName.CropCircleRedesign
  );

  if (isCropCircleRedesignEnabled) {
    return <ActionButtonsCropCircle {...props} />;
  }

  return <ActionButtonsLegacy {...props} />;
};

const ActionButtonsCropCircle = (props: ActionButtonsProps) => {
  const { t } = useTranslation();
  const storageKeys = useStorageKeys();
  const [issueModalVisible, setIssueModalVisible] = useState(false);
  const [newIssueModalProps, setNewIssueModalProps] = useState<
    NewIssueModalProps | undefined
  >(undefined);

  const trackIssueId = async (issueId: string) => {
    const existing = await localStorageHandler.getItem(
      storageKeys.anonymousIssueIds
    );
    const ids: string[] = existing ? JSON.parse(existing) : [];
    if (!ids.includes(issueId)) {
      ids.push(issueId);
    }
    await localStorageHandler.setItem(
      storageKeys.anonymousIssueIds,
      JSON.stringify(ids)
    );
  };

  // Type comes from the form's activityType selection (defaults to Red → Report).
  // We capture it in onIssueCreated and use it to pick the success copy.
  const onIssueCreated = async (issueId: string, type?: IssueType) => {
    await trackIssueId(issueId);
    if (type === IssueType.Note) {
      props.onCompleted(
        t('issue.anonymous.noteAdded'),
        t('issue.anonymous.noteAddedDownloadGrippApp')
      );
    } else {
      props.onCompleted(
        t('issue.anonymous.reported'),
        t('issue.anonymous.reportAddedDownloadGrippApp')
      );
    }
  };

  const onOpenForm = () => {
    setNewIssueModalProps({
      assetId: props.asset.id,
      workspaceId: props.workspaceId,
      isOpened: issueModalVisible,
      modalType: 'modal',
      onComplete: () => setIssueModalVisible(false),
      onIssueCreated,
      onCancel: () => setIssueModalVisible(false),
      createdFrom: IssueCreationSource.AnonymousWeb,
      pickerMode: 'picker',
    });
    setIssueModalVisible(true);
  };

  return (
    <>
      {/* AssetActionButton's inner Button uses `flex: 1`, which on web collapses
          height to content inside the BottomComponentBar's flex-column wrapper.
          The explicit height/maxWidth here gives that flex item a stable box. */}
      <View style={ccStyles.actionWrapper}>
        <AssetActionButton
          onAddNew={onOpenForm}
          buttonContainerStyle={undefined}
          buttonStyle={undefined}
          iconSize={16}
          text={`+ ${t('asset.header.buttons.addNew')}`}
          isCropCircle
          backgroundColor={colors.orange[500]}
          state="active"
        />
      </View>
      {newIssueModalProps && (
        <NewIssueModal
          {...newIssueModalProps}
          isOpened={issueModalVisible}
          hideAdvancedSettings
        />
      )}
    </>
  );
};

const ActionButtonsLegacy = (props: ActionButtonsProps) => {
  const { t } = useTranslation();
  const storageKeys = useStorageKeys();
  const [issueModalVisible, setIssueModalVisible] = useState(false);
  const [newIssueModalProps, setNewIssueModalProps] = useState<
    NewIssueModalProps | undefined
  >(undefined);

  const trackIssueId = async (issueId: string) => {
    const existing = await localStorageHandler.getItem(
      storageKeys.anonymousIssueIds
    );
    const ids: string[] = existing ? JSON.parse(existing) : [];
    if (!ids.includes(issueId)) {
      ids.push(issueId);
    }
    await localStorageHandler.setItem(
      storageKeys.anonymousIssueIds,
      JSON.stringify(ids)
    );
  };

  // Await the storage write before notifying the parent so the OneLink view
  // sees the newly created issue id. The modal's onComplete then just closes
  // the modal — keeping the success copy + storage read in a single async path.
  const makeOnIssueCreated =
    (message: string, messageDownloadApp: string) =>
    async (issueId: string) => {
      await trackIssueId(issueId);
      props.onCompleted(message, messageDownloadApp);
    };

  const onReportIssue = () => {
    setNewIssueModalProps({
      assetId: props.asset.id,
      workspaceId: props.workspaceId!,
      isOpened: issueModalVisible,
      modalType: 'modal',
      onComplete: () => setIssueModalVisible(false),
      onIssueCreated: makeOnIssueCreated(
        t('issue.anonymous.reported'),
        t('issue.anonymous.reportAddedDownloadGrippApp')
      ),
      onCancel: onCancel,
      createdFrom: IssueCreationSource.AnonymousWeb,
      pickerMode: 'picker',
    });
    setIssueModalVisible(true);
  };

  const onNewNote = () => {
    setNewIssueModalProps({
      assetId: props.asset.id,
      workspaceId: props.workspaceId!,
      isOpened: issueModalVisible,
      modalType: 'modal',
      onComplete: () => setIssueModalVisible(false),
      onIssueCreated: makeOnIssueCreated(
        t('issue.anonymous.noteAdded'),
        t('issue.anonymous.noteAddedDownloadGrippApp')
      ),
      onCancel: onCancel,
      createdFrom: IssueCreationSource.AnonymousWeb,
      pickerMode: 'picker',
    });
    setIssueModalVisible(true);
  };

  const onCancel = () => {
    setIssueModalVisible(false);
  };

  return (
    <>
      <View style={legacyStyles.container}>
        <TouchableButton
          color={Colors.white}
          icon={SeverityIcons[Severity.Red]}
          label={t('issue.anonymous.createReport')}
          style={[
            legacyStyles.button,
            legacyStyles.buttonText,
            legacyStyles.report,
            props.buttonStyle,
          ]}
          onPress={onReportIssue}
          titleFontSize={props.titleFontSize}
        />
        <TouchableButton
          color={Colors.white}
          icon={SeverityIcons[Severity.Purple]}
          label={t('issue.anonymous.addNote')}
          style={[
            legacyStyles.button,
            legacyStyles.buttonText,
            legacyStyles.note,
            props.buttonStyle,
          ]}
          onPress={onNewNote}
          titleFontSize={props.titleFontSize}
        />
      </View>
      {newIssueModalProps && (
        <NewIssueModal
          {...newIssueModalProps}
          isOpened={issueModalVisible}
          hideAdvancedSettings
        />
      )}
    </>
  );
};

const ccStyles = StyleSheet.create({
  actionWrapper: {
    flexGrow: 1,
    maxWidth: 320,
    height: 56,
    alignSelf: 'flex-end',
    flexDirection: 'row',
  },
});

const legacyStyles = StyleSheet.create({
  container: {
    flexDirection: 'row',
    gap: 8,
    paddingLeft: 20,
  },
  button: {
    maxWidth: 167,
    height: 81,
    borderRadius: 8,
    marginBottom: 20,
  },
  buttonText: {
    flex: 1,
    flexDirection: 'column',
    lineHeight: 18,
    fontWeight: '500',
    justifyContent: 'center',
    alignItems: 'center',
  },
  report: {
    backgroundColor: Colors.red,
  },
  note: {
    backgroundColor: Colors.purple,
  },
});
